import { useEffect, useMemo, useCallback, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useAppDispatch } from "@store/hook";
import { toggleDREItem, useExpandedDREItems } from "@store/dashboard/dashboardSlice";
import {
  CategoryCardSkeleton,
  DashboardSkeleton,
  DRETableSkeleton,
  FinancialEvolutionChartSkeleton,
} from "@components/Dashboard/Skeletons";
import KpiCardsSkeleton from "@components/Skeletons/KpiCardsSkeleton";
import dashboardService from "@services/dashboard";
import {
  OnboardingModal,
  MonthNavigation,
  KPICards,
  CategoryCard,
  DRETable,
  FinancialEvolutionChart,
  DraggablePanel,
} from "@components/Dashboard";
import { useDashboardOnboarding } from "@hooks/useDashboardOnboarding";
import { useT } from "@hooks";
import { useIntl } from "react-intl";
import PanelShell from "@components/Dashboard/PanelShell";
import { getMonthDateRange, normalizeLast12MonthsData } from "@utils/dateRange";
import type {
  DashboardFilter,
  KPIData,
  ExpenseCategory,
  RevenueCategory,
  DREHierarchicalResponse,
  DREHierarchicalLine,
  DREHierarchicalNode,
  FinancialEvolution,
} from "../../types/dashboard";
import type { ExpandNodeParams } from "@components/Dashboard/DRETable";
import { setEndDate, setStartDate, useGetStoredEndDate, useGetStoredStartDate } from "@store/date/dateSlice";
import { useIsTreeReady } from "@store/tmsAssetTree/tmsAssetTreeSlice";
import { applyStoredFilters, toOptionalListFilters, useStoredFilterValues } from "@hooks/useStoredFilters";
import { useMonthNavigation } from "@hooks/useMonthNavigation";
import SelectedFilters from "@components/SelectedFilters";

type PanelType = "expenses" | "revenue" | "dre" | "chart";
type DragPosition = "left" | "right" | "top" | "bottom";
type DashboardPeriodFilter = DashboardFilter & { startDate: string; endDate: string };

interface Panel {
  id: PanelType;
  order: number;
}

const DEFAULT_PANELS: Panel[] = [
  { id: "dre", order: 0 },
  { id: "expenses", order: 1 },
  { id: "revenue", order: 2 },
  { id: "chart", order: 3 },
];

const DEFAULT_PANEL_PAIRS: string[] = ["expenses|revenue"];

const resolveMomentLocale = (locale: string): string => {
  if (locale.startsWith("en")) return "en-gb";
  if (locale.startsWith("es")) return "es";
  return "pt-br";
};

const removePairsFor = (panelId: PanelType, pairs: Set<string>): Set<string> =>
  new Set(Array.from(pairs).filter((pair) => !pair.startsWith(`${panelId}|`) && !pair.endsWith(`|${panelId}`)));

const keepAdjacentPairs = (pairs: Set<string>, orderIds: PanelType[]): Set<string> => {
  const validPairs = new Set<string>();
  for (let i = 0; i < orderIds.length - 1; i += 1) {
    const pairKey = `${orderIds[i]}|${orderIds[i + 1]}`;
    if (pairs.has(pairKey)) {
      validPairs.add(pairKey);
    }
  }
  return validPairs;
};

const computeNextPairs = (
  prevPairs: string[],
  normalizedPanels: Panel[],
  position: DragPosition,
  dragId: string,
  hoverId: string
): string[] => {
  const orderIds = normalizedPanels.map((panel) => panel.id);
  let nextPairs = new Set(prevPairs);

  const dragPanelId = dragId as PanelType;
  const hoverPanelId = hoverId as PanelType;

  if (position === "left") {
    nextPairs = removePairsFor(dragPanelId, nextPairs);
    nextPairs = removePairsFor(hoverPanelId, nextPairs);
    nextPairs.add(`${dragPanelId}|${hoverPanelId}`);
  }

  if (position === "right") {
    nextPairs = removePairsFor(dragPanelId, nextPairs);
    nextPairs = removePairsFor(hoverPanelId, nextPairs);
    nextPairs.add(`${hoverPanelId}|${dragPanelId}`);
  }

  if (position === "top" || position === "bottom") {
    nextPairs = removePairsFor(dragPanelId, nextPairs);
  }

  return Array.from(keepAdjacentPairs(nextPairs, orderIds));
};

export default function DashboardScreen() {
  const dispatch = useAppDispatch();
  const expandedDREItems = useExpandedDREItems();
  const navigate = useNavigate();
  const t = useT();
  const { locale } = useIntl();
  const momentLocale = resolveMomentLocale(locale);
  moment.locale(momentLocale);

  const {
    showOnboarding,
    onboardingStep,
    onboarding,
    handleCloseOnboarding,
    handleNextStep,
    handlePrevStep,
    handleFilialNameChange,
    handleNumFiliaisChange,
    onboardingLoading,
    onboardingSaving,
  } = useDashboardOnboarding();

  // Filtros do sidebar (Redux)
  const storedFilters = useStoredFilterValues();
  const isTreeReady = useIsTreeReady();

  const startDate = useGetStoredStartDate();
  const endDate = useGetStoredEndDate();

  const { showDateRangePicker, setShowDateRangePicker, selectedMonth, currentMonth, goToPrevMonth, goToNextMonth } =
    useMonthNavigation({ startDate, endDate, momentLocale, dispatch });

  const [panels, setPanels] = useState<Panel[]>(DEFAULT_PANELS);
  const [panelPairs, setPanelPairs] = useState<string[]>(DEFAULT_PANEL_PAIRS);
  const [kpi, setKpi] = useState<KPIData | null>(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [previousKpi, setPreviousKpi] = useState<KPIData | null>(null);
  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[] | null>(null);
  const [expenseCategoriesLoading, setExpenseCategoriesLoading] = useState(true);
  const [revenueCategories, setRevenueCategories] = useState<RevenueCategory[] | null>(null);
  const [revenueCategoriesLoading, setRevenueCategoriesLoading] = useState(true);
  const [dre, setDre] = useState<DREHierarchicalResponse | null>(null);
  const [dreLoading, setDreLoading] = useState(true);
  const [dreLoadingNodes, setDreLoadingNodes] = useState<string[]>([]);
  const [financialEvolution, setFinancialEvolution] = useState<FinancialEvolution[] | null>(null);
  const [financialEvolutionLoading, setFinancialEvolutionLoading] = useState(true);

  const isDateFilterIncomplete = Boolean((startDate && !endDate) || (!startDate && endDate));
  const monthRange = useMemo(() => getMonthDateRange(selectedMonth), [selectedMonth]);
  const requestFilters = useMemo<DashboardPeriodFilter>(() => {
    const useCustomRange = Boolean(startDate && endDate);

    return applyStoredFilters<DashboardPeriodFilter>(
      {
        startDate: useCustomRange ? startDate : monthRange.startDate,
        endDate: useCustomRange ? endDate : monthRange.endDate,
      },
      storedFilters
    );
  }, [monthRange, startDate, endDate, storedFilters]);

  // Filtros do mês anterior, usados apenas para calcular a variação % dos KPIs
  // (o backend ainda não expõe esse comparativo diretamente).
  const previousMonthFilters = useMemo<DashboardFilter>(() => {
    const referenceStart = moment(requestFilters.startDate, "YYYY-MM-DD");
    const previousStart = referenceStart.clone().subtract(1, "month").startOf("month");
    const previousEnd = previousStart.clone().endOf("month");

    return {
      ...requestFilters,
      startDate: previousStart.format("YYYY-MM-DD"),
      endDate: previousEnd.format("YYYY-MM-DD"),
    };
  }, [requestFilters]);

  const fetchKPI = useCallback(async () => {
    if (isDateFilterIncomplete) return;
    setKpiLoading(true);
    try {
      const data = await dashboardService.getKPI(requestFilters);
      setKpi(data);
    } catch (error) {
      console.error("Error fetching KPI:", error);
    } finally {
      setKpiLoading(false);
    }
  }, [requestFilters, isDateFilterIncomplete]);

  const fetchPreviousKPI = useCallback(async () => {
    if (isDateFilterIncomplete) return;
    try {
      const data = await dashboardService.getKPI(previousMonthFilters);
      setPreviousKpi(data);
    } catch (error) {
      console.error("Error fetching previous month KPI:", error);
      setPreviousKpi(null);
    }
  }, [previousMonthFilters, isDateFilterIncomplete]);

  const fetchExpenseCategories = useCallback(async () => {
    if (isDateFilterIncomplete) return;
    setExpenseCategoriesLoading(true);
    try {
      const data = await dashboardService.getExpenseCategories(requestFilters);
      setExpenseCategories(data);
    } catch (error) {
      console.error("Error fetching expense categories:", error);
      setExpenseCategories([]);
    } finally {
      setExpenseCategoriesLoading(false);
    }
  }, [requestFilters, isDateFilterIncomplete]);

  const fetchRevenueCategories = useCallback(async () => {
    if (isDateFilterIncomplete) return;
    setRevenueCategoriesLoading(true);
    try {
      const data = await dashboardService.getRevenueCategories(requestFilters);
      setRevenueCategories(data);
    } catch (error) {
      console.error("Error fetching revenue categories:", error);
      setRevenueCategories([]);
    } finally {
      setRevenueCategoriesLoading(false);
    }
  }, [requestFilters, isDateFilterIncomplete]);

  const fetchDRE = useCallback(async () => {
    if (isDateFilterIncomplete) return;
    setDreLoading(true);
    try {
      const data = await dashboardService.getDRE(requestFilters);
      setDre(data);
    } catch (error) {
      console.error("Error fetching DRE data:", error);
    } finally {
      setDreLoading(false);
    }
  }, [requestFilters, isDateFilterIncomplete]);

  const fetchFinancialEvolution = useCallback(async () => {
    setFinancialEvolutionLoading(true);
    try {
      const filtersWithoutDate: DashboardFilter = toOptionalListFilters(storedFilters);
      const data = await dashboardService.getFinancialEvolutionLast12Months(filtersWithoutDate);
      const normalizedData = normalizeLast12MonthsData(
        data,
        { expenses: 0, revenue: 0, profit: 0, profitMargin: 0 },
        locale
      );
      setFinancialEvolution(normalizedData);
    } catch (error) {
      console.error("Error fetching financial evolution data:", error);
    } finally {
      setFinancialEvolutionLoading(false);
    }
  }, [storedFilters, locale]);

  useEffect(() => {
    if (!isTreeReady) return;
    fetchKPI();
  }, [fetchKPI, isTreeReady]);

  useEffect(() => {
    if (!isTreeReady) return;
    fetchPreviousKPI();
  }, [fetchPreviousKPI, isTreeReady]);

  useEffect(() => {
    if (!isTreeReady) return;
    fetchExpenseCategories();
  }, [fetchExpenseCategories, isTreeReady]);

  useEffect(() => {
    if (!isTreeReady) return;
    fetchRevenueCategories();
  }, [fetchRevenueCategories, isTreeReady]);

  useEffect(() => {
    if (!isTreeReady) return;
    fetchDRE();
  }, [fetchDRE, isTreeReady]);

  useEffect(() => {
    if (!isTreeReady) return;
    fetchFinancialEvolution();
  }, [fetchFinancialEvolution, isTreeReady]);

  const toggleDRE = (id: string) => {
    dispatch(toggleDREItem(id));
  };

  const updateNodeInChildren = useCallback(
    (nodes: DREHierarchicalNode[], nodeId: string, newChildren: DREHierarchicalNode[]): DREHierarchicalNode[] => {
      return nodes.map((node) => {
        if (node.id === nodeId) {
          return { ...node, children: newChildren };
        }
        if (node.children && node.children.length > 0) {
          const updatedChildren = updateNodeInChildren(node.children, nodeId, newChildren);
          const wasUpdated = updatedChildren.some((child, idx) => child !== node.children![idx]);
          if (wasUpdated) {
            return { ...node, children: updatedChildren };
          }
        }
        return node;
      });
    },
    []
  );

  const updateNodeChildren = useCallback(
    (lines: DREHierarchicalLine[], nodeId: string, children: DREHierarchicalNode[]): DREHierarchicalLine[] => {
      const newLines = lines.map((line) => {
        if (line.children) {
          const updatedChildren = updateNodeInChildren(line.children, nodeId, children);
          const wasUpdated = updatedChildren.some((child, idx) => child !== line.children![idx]);
          if (wasUpdated) {
            return { ...line, children: updatedChildren };
          }
        }
        return line;
      });
      return newLines;
    },
    [updateNodeInChildren]
  );

  const handleExpandDRENode = useCallback(
    async (params: ExpandNodeParams) => {
      const { nodeId, compositeId, nodeType, lineType, parentPath } = params;

      setDreLoadingNodes((prev) => [...prev, compositeId]);

      try {
        const response = await dashboardService.expandDRENode(requestFilters, {
          nodeId,
          nodeType,
          lineType,
          parentPath,
        });

        if (response.children && response.children.length > 0) {
          setDre((prevDre) => {
            if (!prevDre) {
              return prevDre;
            }
            const newLines = updateNodeChildren(prevDre.lines, compositeId, response.children);
            return {
              ...prevDre,
              lines: newLines,
            };
          });
        }
      } catch (error) {
        console.error("Error expanding DRE node:", error);
      } finally {
        setDreLoadingNodes((prev) => prev.filter((id) => id !== compositeId));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [requestFilters, updateNodeChildren]
  );

  const handleExportDRE = useCallback(
    async (view: "general" | "branch" | "segment" | "vehicle" | "driver") => {
      try {
        await dashboardService.exportDRE(requestFilters, view);
      } catch (error) {
        console.error("Error exporting DRE:", error);
      }
    },
    [requestFilters]
  );

  const movePanelTo = useCallback(
    (dragIndex: number, hoverIndex: number, position: DragPosition, dragId: string, hoverId: string) => {
      const insertBefore = position === "left" || position === "top";
      let targetIndex = insertBefore ? hoverIndex : hoverIndex + 1;
      if (dragIndex < targetIndex) {
        targetIndex -= 1;
      }

      setPanels((prevPanels) => {
        const newPanels = [...prevPanels];
        const [draggedPanel] = newPanels.splice(dragIndex, 1);
        newPanels.splice(targetIndex, 0, draggedPanel);
        const normalizedPanels = newPanels.map((panel, index) => ({ ...panel, order: index }));

        setPanelPairs((prevPairs) => computeNextPairs(prevPairs, normalizedPanels, position, dragId, hoverId));

        return normalizedPanels;
      });

      return targetIndex;
    },
    []
  );

  const buildPeriodQuery = (extraParams: Record<string, string> = {}) => {
    const params = new URLSearchParams({
      startDate: requestFilters.startDate,
      endDate: requestFilters.endDate,
      ...extraParams,
    });
    return `?${params.toString()}`;
  };

  const renderPanel = (panelId: PanelType) => {
    switch (panelId) {
      case "expenses":
        return (
          <PanelShell
            loading={expenseCategoriesLoading}
            loadingLabel={t("dashboard.categories.expenses")}
            hasData
            skeleton={<CategoryCardSkeleton />}
          >
            <CategoryCard
              addButtonText={t("dashboard.button.newExpense")}
              title={t("dashboard.categories.expenses")}
              categories={expenseCategories ?? []}
              onViewClick={() => navigate(`/trucker-pay-gestor/expenses${buildPeriodQuery()}`)}
              onAddClick={() => navigate(`/trucker-pay-gestor/expenses${buildPeriodQuery({ add: "expense" })}`)}
            />
          </PanelShell>
        );
      case "revenue":
        return (
          <PanelShell
            loading={revenueCategoriesLoading}
            loadingLabel={t("dashboard.categories.revenue")}
            hasData
            skeleton={<CategoryCardSkeleton />}
          >
            <CategoryCard
              addButtonText={t("dashboard.button.newRevenue")}
              title={t("dashboard.categories.revenue")}
              categories={revenueCategories ?? []}
              onViewClick={() => navigate(`/trucker-pay-gestor/recipes${buildPeriodQuery()}`)}
              onAddClick={() => navigate(`/trucker-pay-gestor/recipes${buildPeriodQuery({ add: "receipt" })}`)}
            />
          </PanelShell>
        );
      case "dre":
        return (
          <PanelShell
            loading={dreLoading}
            loadingLabel={t("dashboard.dre.title")}
            hasData={Boolean(dre?.lines?.length)}
            skeleton={<DRETableSkeleton />}
          >
            <DRETable
              data={dre?.lines ?? []}
              expandedItems={expandedDREItems}
              loadingNodes={dreLoadingNodes}
              onToggle={toggleDRE}
              onExpandNode={handleExpandDRENode}
              onExportDRE={handleExportDRE}
            />
          </PanelShell>
        );
      case "chart":
        return (
          <PanelShell
            loading={financialEvolutionLoading}
            loadingLabel={t("dashboard.evolution.title")}
            hasData={Boolean(financialEvolution?.length)}
            skeleton={<FinancialEvolutionChartSkeleton />}
          >
            <FinancialEvolutionChart data={financialEvolution ?? []} />
          </PanelShell>
        );
    }
  };

  const renderRows = () => {
    const sortedPanels = [...panels].sort((a, b) => a.order - b.order);
    const rows: React.ReactElement[] = [];
    const pairSet = new Set(panelPairs);
    let i = 0;

    while (i < sortedPanels.length) {
      const currentPanel = sortedPanels[i];
      const nextPanel = sortedPanels[i + 1];
      const shouldPair = Boolean(nextPanel) && pairSet.has(`${currentPanel.id}|${nextPanel.id}`);

      if (shouldPair && nextPanel) {
        rows.push(
          <div key={`row-${i}`} className="display-grid grid-cols-1 grid-cols-2-md gap-10 gap-20-sm margin-bottom-20">
            <DraggablePanel id={currentPanel.id} index={i} movePanelTo={movePanelTo}>
              {renderPanel(currentPanel.id)}
            </DraggablePanel>
            <DraggablePanel id={nextPanel.id} index={i + 1} movePanelTo={movePanelTo}>
              {renderPanel(nextPanel.id)}
            </DraggablePanel>
          </div>
        );
        i += 2;
        continue;
      }

      rows.push(
        <div key={`row-${i}`} className="margin-bottom-20">
          <DraggablePanel id={currentPanel.id} index={i} movePanelTo={movePanelTo}>
            {renderPanel(currentPanel.id)}
          </DraggablePanel>
        </div>
      );
      i += 1;
    }

    return rows;
  };

  if (onboardingLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="width-100pct height-100pct display-flex flex-column bg-lighest">
        <OnboardingModal
          show={showOnboarding}
          step={onboardingStep}
          onNext={handleNextStep}
          onPrev={handlePrevStep}
          isSaving={onboardingSaving}
          onClose={handleCloseOnboarding}
          numFiliais={onboarding.numFiliais}
          filiaisNames={onboarding.filiaisNames}
          onNumFiliaisChange={handleNumFiliaisChange}
          onFilialNameChange={handleFilialNameChange}
        />

        <div className="flex-1 overflow-y-auto">
          <div className="display-flex flex-column gap-10 gap-20-sm padding-10 padding-25-sm bg-light">
            <MonthNavigation
              endDate={endDate}
              startDate={startDate}
              currentMonth={currentMonth}
              onPrevMonth={goToPrevMonth}
              onNextMonth={goToNextMonth}
              onEndDateChange={setEndDate}
              onStartDateChange={setStartDate}
              showDateRangePicker={showDateRangePicker}
              onClick={() => setShowDateRangePicker(!showDateRangePicker)}
              onCloseDateRangePicker={() => setShowDateRangePicker(false)}
            />

            <SelectedFilters requestFilters={requestFilters} />
          </div>

          <div className="padding-10 padding-25-sm">
            <PanelShell
              loading={kpiLoading}
              loadingLabel="KPIs"
              hasData={Boolean(kpi)}
              className="margin-bottom-20"
              skeleton={<KpiCardsSkeleton />}
            >
              {kpi && (
                <KPICards
                  kpi={kpi}
                  previousKpi={previousKpi}
                  startDate={requestFilters.startDate}
                  endDate={requestFilters.endDate}
                />
              )}
            </PanelShell>

            {renderRows()}
          </div>
        </div>
      </div>
    </DndProvider>
  );
}
